import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { ErrorMessage, InfoMessage } from '@eika/attention-message';
import { StandardButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { SearchInput } from '@eika/input';

import {
    FormTypeCalculatorVehicleInfo,
    FormTypeEuroTaxVehicleInfo,
    fetchObjectCalculatorDataAction,
    fetchOngoingApplicationEuroTax,
    FormTypeLoanSearchForm,
} from '../../actions';
import Loader from '../../components/Loader/Loader';
import { CustomerCaseInfoLandingPage } from '../../generated-types';
import { AppState } from '../../reducers';
import { errorNames } from '../../util/mappings/errorName';
import LoanProductRegValidations, { LoanProductRegValid } from '../../util/mappings/loanProductRegValidations';
import { euroTaxSearchSchema } from '../../validation/loanSearchCarSchema';

interface Props {
    application: CustomerCaseInfoLandingPage;
}

const LoanSearchForm = ({ application }: Props): JSX.Element => {
    const dispatch = useDispatch<any>();
    const { context, ongoingApplications, loader } = useSelector((state: AppState) => state);
    const { data } = context.config;
    const { text } = data;
    const { applicationId, loanProduct } = application;
    const { selectedError } = ongoingApplications;
    const eurotaxPlateNotFound = selectedError && selectedError === errorNames.eurotaxPlateNotFound;

    const regNrValidateObject: LoanProductRegValid = LoanProductRegValidations(loanProduct);
    const vehicleRegistrationNumberLength = regNrValidateObject.max;

    useEffect(() => {
        if (loanProduct) {
            dispatch(fetchObjectCalculatorDataAction(loanProduct));
        }
    }, [loanProduct, dispatch]);

    const {
        handleSubmit,
        register,
        setValue,
        formState: { errors },
    } = useForm<FormTypeLoanSearchForm>({
        resolver: yupResolver(euroTaxSearchSchema(regNrValidateObject)),
        shouldUnregister: true,
        mode: 'all',
        defaultValues: {},
    });

    if (loader.isFetchingVehicleInfo)
        return (
            <div className="vehicle-info eurotax-search-form">
                <Loader title={text.loadingDefault} />
            </div>
        );

    return (
        <>
            <div className="vehicle-info eurotax-search-form">
                <form
                    onSubmit={handleSubmit((form: FormTypeEuroTaxVehicleInfo) => {
                        dispatch(
                            fetchOngoingApplicationEuroTax(
                                {
                                    vehicleRegistrationNumber: form.regNr,
                                } as FormTypeCalculatorVehicleInfo,
                                applicationId,
                            ),
                        );
                    })}
                >
                    <div className="vehicle-info__odometer input-wrapper">
                        <InfoMessage
                            className="loan-info__info-message"
                            icon={IconTypes.LYSPAERE_24}
                            text={text.eurotaxSearchErrorText1}
                        />
                        <SearchInput
                            {...register('regNr')}
                            label={text.eurotaxRegNumber}
                            type="text"
                            minLength={vehicleRegistrationNumberLength}
                            error={errors?.regNr?.message || ''}
                            onChange={(e: any) => {
                                setValue(
                                    e.target.name,
                                    e.target.value
                                        .replace(/[^\d\w]/g, '')
                                        .substring(0, vehicleRegistrationNumberLength)
                                        .toUpperCase(),
                                );
                            }}
                        />
                    </div>
                    <div className="vehicle-info__button">
                        <StandardButton type="submit">{text.eurotaxSearchButton}</StandardButton>
                    </div>
                    {eurotaxPlateNotFound && (
                        <ErrorMessage
                            className="vehicle-info__error-message"
                            icon={IconTypes.LUKK_24}
                            text={text.eurotaxPlateNotFoundWarning}
                        />
                    )}
                </form>
            </div>
        </>
    );
};
export default LoanSearchForm;
